import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { API_BASE } from '../../config';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  ADMIN_LABEL_CLASSNAME,
  ADMIN_TABLE_LINK_CLASSNAME,
} from './admin-ui';

export interface StyleNotesViewContent {
  title: string;
  styleNotes?: string | null;
  styleNotesBefore?: string | null;
  styleNotesAfter?: string | null;
  regenerationLogEntryId?: string;
  styleNotesComparison?: string | null;
  styleNotesComparisonLlmCallId?: string | null;
}

interface StyleNotesViewDialogProps {
  content: StyleNotesViewContent | null;
  onClose: () => void;
  onComparisonUpdated?: (logEntryId: string, comparison: string, comparisonLlmCallId: string | null) => void;
}

const renderNotesBlock = (notes: string | null | undefined, t: (key: string) => string) => {
  const trimmed = notes?.trim();
  if (!trimmed) {
    return (
      <p className="m-0 text-sm italic text-muted-foreground">
        {t('admin_style_notes_no_notes')}
      </p>
    );
  }

  return (
    <div className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 text-sm text-foreground">
      {trimmed}
    </div>
  );
};

export const StyleNotesViewDialog: React.FC<StyleNotesViewDialogProps> = ({
  content,
  onClose,
  onComparisonUpdated,
}) => {
  const { t } = useLocale();
  const { token, masterKey } = useAuth();
  const [comparison, setComparison] = useState<string | null>(null);
  const [comparisonLlmCallId, setComparisonLlmCallId] = useState<string | null>(null);
  const [comparing, setComparing] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);

  const hasBeforeAfter = content?.styleNotesBefore !== undefined || content?.styleNotesAfter !== undefined;
  const canCompare = !!content?.regenerationLogEntryId && !!content.styleNotesAfter?.trim();

  useEffect(() => {
    setComparison(content?.styleNotesComparison?.trim() || null);
    setComparisonLlmCallId(content?.styleNotesComparisonLlmCallId ?? null);
    setCompareError(null);
    setComparing(false);
  }, [content]);

  const handleCompare = async () => {
    if (!content?.regenerationLogEntryId || (!token && !masterKey)) {
      return;
    }

    setComparing(true);
    setCompareError(null);

    try {
      const params = new URLSearchParams();
      if (masterKey) params.set('masterKey', masterKey);

      const url = `${API_BASE}/api/admin/style-notes/regeneration-log/${content.regenerationLogEntryId}/compare?${params.toString()}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: token && !masterKey ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to compare style notes');
      }

      const data = await response.json();
      const nextComparison = data.entry.styleNotesComparison?.trim() || null;
      setComparison(nextComparison);
      setComparisonLlmCallId(data.entry.styleNotesComparisonLlmCallId ?? null);

      if (nextComparison && onComparisonUpdated) {
        onComparisonUpdated(
          content.regenerationLogEntryId,
          nextComparison,
          data.entry.styleNotesComparisonLlmCallId ?? null
        );
      }
    } catch (error) {
      console.error('Error comparing style notes:', error);
      setCompareError(t('admin_style_notes_compare_error'));
    } finally {
      setComparing(false);
    }
  };

  return (
    <Dialog open={content !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{content?.title ?? t('admin_style_notes_column')}</DialogTitle>
        </DialogHeader>

        {content && (
          <div className="grid gap-4">
            {hasBeforeAfter ? (
              <>
                {canCompare && !comparison && (
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void handleCompare()}
                      disabled={comparing}
                    >
                      {comparing ? t('admin_style_notes_comparing') : t('admin_style_notes_compare')}
                    </Button>
                    {compareError && (
                      <span className="text-sm text-destructive">{compareError}</span>
                    )}
                  </div>
                )}

                {comparison && (
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className={`m-0 ${ADMIN_LABEL_CLASSNAME}`}>
                        {t('admin_style_notes_comparison')}
                      </h4>
                      {comparisonLlmCallId && (
                        <Link
                          to={`/admin/llm-calls?callId=${comparisonLlmCallId}`}
                          className={`text-xs ${ADMIN_TABLE_LINK_CLASSNAME}`}
                        >
                          {t('admin_style_notes_view_llm_call')}
                        </Link>
                      )}
                    </div>
                    {renderNotesBlock(comparison, t)}
                  </div>
                )}

                <div>
                  <h4 className={`mb-2 ${ADMIN_LABEL_CLASSNAME}`}>
                    {t('admin_style_notes_before')}
                  </h4>
                  {renderNotesBlock(content.styleNotesBefore, t)}
                </div>
                <div>
                  <h4 className={`mb-2 ${ADMIN_LABEL_CLASSNAME}`}>
                    {t('admin_style_notes_after')}
                  </h4>
                  {renderNotesBlock(content.styleNotesAfter, t)}
                </div>
              </>
            ) : (
              renderNotesBlock(content.styleNotes, t)
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
